import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/components/feature/Toast';
import { useDataRefresh } from '@/hooks/useDataRefresh';
import { adjustCustomerDebt, getCustomerById, getSalesByCustomerId, getPromissoryNotesByCustomerId } from '@/lib/supabaseDb';
import { formatTRY, parseCurrencyInput } from '@/lib/currencyFormat';
import ResponsiveFormModal from '@/components/base/ResponsiveFormModal';

interface DebtAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerId: string;
  customerName: string;
  currentDebt: number;
}

type LinkType = 'direct' | 'sale' | 'note';

export default function DebtAdjustmentModal({ isOpen, onClose, customerId, customerName, currentDebt }: DebtAdjustmentModalProps) {
  const { user, profile, role } = useAuth();
  const { showToast } = useToast();
  const { triggerRefresh } = useDataRefresh();

  const [newTotalDebt, setNewTotalDebt] = useState('');
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [linkType, setLinkType] = useState<LinkType>('direct');
  const [linkedSaleId, setLinkedSaleId] = useState('');
  const [linkedNoteId, setLinkedNoteId] = useState('');
  const [sales, setSales] = useState<any[]>([]);
  const [notes, setNotes] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isAdmin = role === 'admin';

  const newDebtNum = parseFloat(parseCurrencyInput(newTotalDebt)) || 0;
  const difference = newTotalDebt.trim() !== '' ? newDebtNum - currentDebt : 0;

  useEffect(() => {
    if (!isOpen || !customerId) return;
    setNewTotalDebt('');
    setReason('');
    setDescription('');
    setLinkType('direct');
    setLinkedSaleId('');
    setLinkedNoteId('');
    setErrors({});
    setSaving(false);
    setShowConfirm(false);

    Promise.all([
      getSalesByCustomerId(customerId),
      getPromissoryNotesByCustomerId(customerId),
    ]).then(([salesRes, notesRes]) => {
      setSales((salesRes.data || []).filter((s: any) => (s.remaining_amount || 0) > 0));
      setNotes((notesRes.data || []).filter((n: any) => {
        const rem = n.remaining_amount !== undefined ? n.remaining_amount : ((n.amount || 0) - (n.paid_amount || 0));
        return rem > 0 && n.status !== 'İptal' && n.status !== 'Ödendi';
      }));
    }).catch(() => {});
  }, [isOpen, customerId]);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!newTotalDebt.trim() || isNaN(newDebtNum) || newDebtNum < 0) errs.new_debt = 'Geçerli bir borç tutarı giriniz.';
    if (!reason.trim()) errs.reason = 'Düzeltme nedeni zorunludur.';
    if (newDebtNum === currentDebt) errs.new_debt = 'Yeni borç mevcut borç ile aynı. Değişiklik yok.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSubmitClick() {
    if (!validate()) return;
    setShowConfirm(true);
  }

  async function handleConfirm() {
    if (saving) return;
    setSaving(true);
    setShowConfirm(false);

    try {
      const result = await adjustCustomerDebt({
        customer_id: customerId,
        new_total_debt: newDebtNum,
        reason: reason.trim(),
        description: description.trim(),
        processed_by: user?.id || '',
        processed_by_name: profile?.full_name || 'Bilinmeyen',
        linked_sale_id: linkType === 'sale' ? linkedSaleId : null,
        linked_note_id: linkType === 'note' ? linkedNoteId : null,
      });

      if (result.error) {
        showToast(result.error, 'error');
        setSaving(false);
        return;
      }

      showToast('Borç düzeltmesi başarıyla kaydedildi.', 'success');
      triggerRefresh();
      onClose();
    } catch {
      showToast('Borç düzeltmesi sırasında hata oluştu.', 'error');
    } finally {
      setSaving(false);
    }
  }

  const inputClass = 'w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 placeholder:text-foreground-300';
  const inputErrorClass = 'w-full px-3 py-2.5 text-sm bg-red-50 border border-red-300 rounded-lg focus:outline-none focus:border-red-400 focus:ring-1 focus:ring-red-200 text-foreground-800';
  const selectClass = 'w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 focus:ring-1 focus:ring-primary-200 text-foreground-800 cursor-pointer';
  const labelClass = 'text-xs font-medium text-foreground-500 mb-1.5 block';
  const readonlyClass = 'w-full px-3 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg text-foreground-600';
  const errorTextClass = 'text-xs text-red-500 mt-1';

  const headerContent = (
    <div className="flex items-center justify-between px-5 py-3.5">
      <div className="flex items-center gap-3">
        <span className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${difference < 0 ? 'bg-emerald-500' : difference > 0 ? 'bg-red-500' : 'bg-secondary-500'}`}>
          <i className={`${difference < 0 ? 'ri-arrow-down-line' : difference > 0 ? 'ri-arrow-up-line' : 'ri-scales-3-line'} text-background-50 text-sm`}></i>
        </span>
        <div>
          <h3 className="text-base font-semibold text-foreground-900">Borç Düzeltme</h3>
          <p className="text-xs text-foreground-400">{customerName}</p>
        </div>
      </div>
      <button onClick={onClose} disabled={saving} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 cursor-pointer disabled:opacity-50">
        <i className="ri-close-line"></i>
      </button>
    </div>
  );

  const footerContent = (
    <div className="flex items-center justify-end gap-3 px-5 py-3">
      <button onClick={onClose} disabled={saving} className="btn-outline cursor-pointer whitespace-nowrap disabled:opacity-50 text-sm">İptal</button>
      <button onClick={handleSubmitClick} disabled={saving} className="btn-primary flex items-center gap-2 cursor-pointer whitespace-nowrap disabled:opacity-60 text-sm">
        {saving ? <><i className="ri-loader-4-line animate-spin"></i>Kaydediliyor...</> : <><i className="ri-check-line"></i>Devam</>}
      </button>
    </div>
  );

  const confirmOverlay = (
    <div className="bg-background-50 rounded-xl border border-background-200 shadow-xl p-6 mx-6 max-w-sm w-full">
      <div className="flex items-center gap-3 mb-4">
        <span className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
          <i className="ri-alert-line text-amber-600 text-lg"></i>
        </span>
        <div>
          <h4 className="text-sm font-semibold text-foreground-900">Düzeltmeyi Onayla</h4>
          <p className="text-xs text-foreground-500 mt-0.5">Bu işlem geri alınamaz.</p>
        </div>
      </div>
      <div className="bg-background-100 rounded-lg p-3 mb-4 text-sm space-y-1.5">
        <div className="flex justify-between"><span className="text-foreground-500">Mevcut Borç:</span><span className="font-medium text-foreground-800">{formatTRY(currentDebt)}</span></div>
        <div className="flex justify-between"><span className="text-foreground-500">Yeni Borç:</span><span className="font-medium text-foreground-800">{formatTRY(newDebtNum)}</span></div>
        <div className="flex justify-between border-t border-background-200 pt-1.5 mt-1">
          <span className="text-foreground-500">Fark:</span>
          <span className={`font-semibold ${difference < 0 ? 'text-emerald-600' : difference > 0 ? 'text-red-600' : 'text-foreground-500'}`}>
            {difference < 0 ? '-' : '+'}{formatTRY(Math.abs(difference))}
          </span>
        </div>
        {reason && <div className="flex justify-between border-t border-background-200 pt-1.5"><span className="text-foreground-500">Neden:</span><span className="font-medium text-foreground-800 text-right max-w-[60%]">{reason}</span></div>}
        <div className="text-xs text-foreground-400 border-t border-background-200 pt-1.5">Fark tüm açık satış ve senetlere orantılı dağıtılacak.</div>
      </div>
      <div className="flex gap-2">
        <button onClick={() => setShowConfirm(false)} disabled={saving} className="flex-1 py-2 px-3 text-sm rounded-lg border border-background-200 text-foreground-600 hover:bg-background-100 cursor-pointer whitespace-nowrap">İptal</button>
        <button onClick={handleConfirm} disabled={saving} className="flex-1 py-2 px-3 text-sm rounded-lg bg-primary-500 text-background-50 hover:bg-primary-600 cursor-pointer whitespace-nowrap disabled:opacity-50 flex items-center justify-center gap-1.5">
          {saving ? <><i className="ri-loader-4-line animate-spin"></i>İşleniyor...</> : 'Düzeltmeyi Onayla'}
        </button>
      </div>
    </div>
  );

  if (!isAdmin) {
    return (
      <ResponsiveFormModal isOpen={isOpen} onClose={onClose} zIndex={70} header={headerContent} footer={
        <div className="flex items-center justify-end gap-3 px-5 py-3">
          <button onClick={onClose} className="btn-outline cursor-pointer whitespace-nowrap text-sm">Kapat</button>
        </div>
      }>
        <div className="px-5 py-8 flex flex-col items-center justify-center text-center">
          <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mb-3">
            <i className="ri-shield-check-line text-2xl text-red-400"></i>
          </div>
          <h4 className="text-base font-semibold text-foreground-900">Yetkiniz Yok</h4>
          <p className="text-sm text-foreground-500 mt-1 max-w-xs">Borç düzeltme işlemi yalnızca Admin ve Yönetici yetkisine sahip kullanıcılar tarafından yapılabilir.</p>
        </div>
      </ResponsiveFormModal>
    );
  }

  return (
    <ResponsiveFormModal
      isOpen={isOpen}
      onClose={onClose}
      zIndex={70}
      header={headerContent}
      footer={footerContent}
      overlayContent={showConfirm ? confirmOverlay : null}
    >
      <div className="px-5 py-4">
        <div className="space-y-4">
          {/* Mevcut Borç (readonly) */}
          <div>
            <label className={labelClass}>Mevcut Toplam Borç</label>
            <div className={readonlyClass}>{formatTRY(currentDebt)}</div>
          </div>

          {/* Yeni Toplam Borç */}
          <div>
            <label className={labelClass}>Yeni Toplam Borç <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={newTotalDebt}
              onChange={e => { setNewTotalDebt(e.target.value); setErrors(prev => ({ ...prev, new_debt: '' })); }}
              className={errors.new_debt ? inputErrorClass : inputClass}
              placeholder="0,00"
            />
            {errors.new_debt && <p className={errorTextClass}>{errors.new_debt}</p>}
          </div>

          {/* Düzeltme Farkı (auto-calculated) */}
          <div>
            <label className={labelClass}>Düzeltme Farkı</label>
            <div className={`${readonlyClass} flex items-center gap-2 ${difference < 0 ? 'text-emerald-600 bg-emerald-50 border-emerald-200' : difference > 0 ? 'text-red-600 bg-red-50 border-red-200' : ''}`}>
              {newTotalDebt.trim() !== '' ? (
                <>
                  <i className={`${difference < 0 ? 'ri-arrow-down-line' : difference > 0 ? 'ri-arrow-up-line' : 'ri-subtract-line'} text-sm`}></i>
                  <span className="font-medium">{difference < 0 ? '-' : '+'}{formatTRY(Math.abs(difference))}</span>
                  {difference < 0 && <span className="text-xs text-emerald-500 font-medium ml-auto">Borç Azaltma</span>}
                  {difference > 0 && <span className="text-xs text-red-500 font-medium ml-auto">Borç Artırma</span>}
                </>
              ) : (
                <span className="text-foreground-400">Yeni borç girildiğinde otomatik hesaplanır</span>
              )}
            </div>
          </div>

          {/* Düzeltme Nedeni */}
          <div>
            <label className={labelClass}>Düzeltme Nedeni <span className="text-red-400">*</span></label>
            <select
              value={reason}
              onChange={e => { setReason(e.target.value); setErrors(prev => ({ ...prev, reason: '' })); }}
              className={errors.reason ? `${inputErrorClass} cursor-pointer` : selectClass}
            >
              <option value="">Seçiniz...</option>
              <option value="Faiz Düzeltmesi">Faiz Düzeltmesi</option>
              <option value="İskonto / İndirim">İskonto / İndirim</option>
              <option value="Yanlış Hesaplama Düzeltmesi">Yanlış Hesaplama Düzeltmesi</option>
              <option value="Vade Farkı">Vade Farkı</option>
              <option value="Kısmi İptal / İade">Kısmi İptal / İade</option>
              <option value="Mahsup İşlemi">Mahsup İşlemi</option>
              <option value="Muhasebe Düzeltmesi">Muhasebe Düzeltmesi</option>
              <option value="Açılış Bakiyesi Düzeltme">Açılış Bakiyesi Düzeltme</option>
              <option value="Diğer">Diğer</option>
            </select>
            {errors.reason && <p className={errorTextClass}>{errors.reason}</p>}
          </div>

          {/* Açıklama */}
          <div>
            <label className={labelClass}>Açıklama</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              maxLength={500}
              className={`${inputClass} resize-none h-20`}
              placeholder="Düzeltme ile ilgili açıklama..."
            />
          </div>

          {/* Bilgi: Tüm açık kayıtlar orantılı güncellenir */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2.5">
            <i className="ri-information-line text-amber-500 text-sm mt-0.5 flex-shrink-0"></i>
            <p className="text-xs text-amber-700 leading-relaxed">
              Borç düzeltmesi yapıldığında fark, <strong>tüm açık satış ve senetlere orantılı olarak dağıtılır</strong>. Her bir kaydın kalan tutarı, toplam borç içindeki payı oranında güncellenir.
            </p>
          </div>

          {/* Bağlantı Türü (isteğe bağlı - sadece kayıt tutma amaçlı) */}
          <div>
            <label className={labelClass}>Referans Kaydı <span className="text-foreground-300 font-normal">(isteğe bağlı)</span></label>
            <div className="flex gap-2 mb-2">
              {(['direct', 'sale', 'note'] as LinkType[]).map(lt => (
                <button
                  key={lt}
                  onClick={() => { setLinkType(lt); setLinkedSaleId(''); setLinkedNoteId(''); setErrors(prev => ({ ...prev, link: '' })); }}
                  className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium cursor-pointer transition-colors whitespace-nowrap ${
                    linkType === lt
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-500 hover:bg-background-200'
                  }`}
                >
                  {lt === 'direct' ? 'Yok' : lt === 'sale' ? 'Satış' : 'Senet'}
                </button>
              ))}
            </div>
            <p className="text-xs text-foreground-300">Sadece kayıt tutma amaçlıdır, düzeltme işlemini etkilemez.</p>
          </div>

          {/* Satış Seçici */}
          {linkType === 'sale' && (
            <div>
              <label className={labelClass}>Referans Satış</label>
              {sales.length === 0 ? (
                <div className="text-sm text-foreground-400 bg-background-100 rounded-lg p-3">Bu müşteri için açık satış bulunamadı.</div>
              ) : (
                <select
                  value={linkedSaleId}
                  onChange={e => setLinkedSaleId(e.target.value)}
                  className={selectClass}
                >
                  <option value="">Satış seçiniz...</option>
                  {sales.map((s: any) => (
                    <option key={s.id} value={s.id}>{s.sale_no} - {formatTRY(s.remaining_amount || 0)} kalan ({s.sale_date ? new Date(s.sale_date).toLocaleDateString('tr-TR') : ''})</option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* Senet Seçici */}
          {linkType === 'note' && (
            <div>
              <label className={labelClass}>Referans Senet</label>
              {notes.length === 0 ? (
                <div className="text-sm text-foreground-400 bg-background-100 rounded-lg p-3">Bu müşteri için açık senet bulunamadı.</div>
              ) : (
                <select
                  value={linkedNoteId}
                  onChange={e => setLinkedNoteId(e.target.value)}
                  className={selectClass}
                >
                  <option value="">Senet seçiniz...</option>
                  {notes.map((n: any) => {
                    const rem = n.remaining_amount !== undefined ? n.remaining_amount : ((n.amount || 0) - (n.paid_amount || 0));
                    return (
                      <option key={n.id} value={n.id}>{n.note_no} - {formatTRY(rem)} kalan ({n.due_date ? new Date(n.due_date).toLocaleDateString('tr-TR') : ''})</option>
                    );
                  })}
                </select>
              )}
            </div>
          )}

          {/* İşlem Bilgileri (readonly) */}
          <div className="grid grid-cols-2 gap-3 pt-2 border-t border-background-100">
            <div>
              <label className={labelClass}>İşlemi Yapan</label>
              <div className={readonlyClass}>{profile?.full_name || '—'}</div>
            </div>
            <div>
              <label className={labelClass}>İşlem Tarihi</label>
              <div className={readonlyClass}>{new Date().toLocaleDateString('tr-TR')}</div>
            </div>
          </div>
        </div>
      </div>
    </ResponsiveFormModal>
  );
}